import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';
import { getImageUrl, hideBrokenImage } from '../utils/imageUrl';

const BlogList = () => {
  const { user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const blogsPerPage = 10;

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/blogs?page=${currentPage}&limit=${blogsPerPage}&status=published`;
      
      if (selectedCategory) {
        url = `/categories/${selectedCategory}/blogs?page=${currentPage}&limit=${blogsPerPage}`;
      }

      const response = await apiClient.get(url);
      setBlogs(response.data.blogs || response.data.data?.blogs || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch blogs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiClient.get('/categories');
      setCategories(response.data.categories || response.data.data?.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [fetchBlogs, fetchCategories]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      try {
        setLoading(true);
        const response = await apiClient.get(`/search/blogs?q=${searchQuery}`);
        setBlogs(response.data.blogs || response.data.data?.blogs || []);
        setTotalPages(1);
      } catch (error) {
        toast.error('Search failed');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Navigation */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="/fbrlogo1.svg" 
                alt="FBR Logo" 
                className="h-10 w-10 mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                FBRSTAPP
              </Link>
            </div>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900 font-medium">Home</Link>
              <Link to="/blog" className="text-gray-600 hover:text-gray-900 font-medium">Blog</Link>
              {user ? (
                <Link
                  to="/app/dashboard"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-blue-600 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Our Blog</h1>
          <p className="text-xl">Stay updated with the latest insights and news</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search blog posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Search
            </button>
          </div>
        </form>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold mb-4">Categories</h3>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setCurrentPage(1);
                  }}
                  className={`block w-full text-left px-3 py-2 rounded ${
                    selectedCategory === null ? 'bg-blue-100 text-blue-600 font-bold' : 'hover:bg-gray-100'
                  }`}
                >
                  All Posts
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategory(cat.slug);
                      setCurrentPage(1);
                    }}
                    className={`block w-full text-left px-3 py-2 rounded ${
                      selectedCategory === cat.slug ? 'bg-blue-100 text-blue-600 font-bold' : 'hover:bg-gray-100'
                    }`}
                  >
                    {cat.name} ({cat.post_count})
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {loading ? (
              <div className="text-center py-8">Loading blogs...</div>
            ) : blogs.length > 0 ? (
              <>
                <div className="space-y-6">
                  {blogs.map(blog => {
                    const imageUrl = getImageUrl(blog.featured_image);
                    return (
                    <article key={blog.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition">
                      <div className="md:flex">
                        {imageUrl && (
                          <img
                            src={imageUrl}
                            alt={blog.title}
                            className="md:w-1/3 h-64 object-cover"
                            onError={hideBrokenImage}
                          />
                        )}
                        <div className="p-6 flex-1">
                          <Link
                            to={`/blog/${blog.slug}`}
                            className="text-2xl font-bold text-gray-800 hover:text-blue-600"
                          >
                            {blog.title}
                          </Link>
                          <div className="flex gap-4 text-gray-600 text-sm mt-2">
                            <span>By {blog.author_name}</span>
                            <span>{new Date(blog.publish_date).toLocaleDateString()}</span>
                            <span>{blog.views} views</span>
                          </div>
                          {blog.category_name && (
                            <Link
                              to={`/blog/category/${blog.category_slug}`}
                              className="inline-block mt-3 px-3 py-1 bg-blue-100 text-blue-600 rounded text-sm hover:bg-blue-200"
                            >
                              {blog.category_name}
                            </Link>
                          )}
                          <p className="mt-4 text-gray-600 line-clamp-3">
                            {blog.excerpt || blog.content.substring(0, 200)}...
                          </p>
                          <Link
                            to={`/blog/${blog.slug}`}
                            className="inline-block mt-4 text-blue-600 hover:text-blue-800 font-semibold"
                          >
                            Read More →
                          </Link>
                        </div>
                      </div>
                    </article>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 hover:bg-gray-300'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-600">
                No blog posts found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogList;
